import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Package, 
  Users, 
  User, 
  LogOut,
  Menu,
  X,
  Shield,
  Palette,
  FileText,
  Settings,
  BarChart3,
  Calculator,
  Briefcase,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const Sidebar = ({ userType, isOpen, onToggle }) => {
  const { user, logout, isAdmin, isDesigner, isSuperAccountant } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'FinanceBotSup_bot';
  const telegramDeepLink = useMemo(() => (botUsername ? `https://t.me/${botUsername}?start=link` : null), [botUsername]);
  
  // Use external state if provided, otherwise use internal state
  const isMobileOpen = isOpen !== undefined ? isOpen : sidebarOpen;
  const toggleMobile = onToggle || (() => setSidebarOpen(!sidebarOpen));

  // Get navigation based on user type
  const getNavigation = () => {
    if (userType === 'accountant') {
      // Kiểm tra nếu là super_accountant
      if (isSuperAccountant) {
        return [
          { name: 'Thống kê', href: '/accountant/statistics', icon: BarChart3 },
          { name: 'Financial Cases', href: '/accountant/finance', icon: Calculator },
          { name: 'Tạo Financial Case', href: '/accountant/create-financial-case', icon: Calculator },
          { 
            name: 'Work Cases', icon: Briefcase,
            children: [
              { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
              { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
              { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
            ]
          },
          { name: 'Thông tin cá nhân', href: '/accountant/profile', icon: User },
        ];
      } else {
        return [
          { name: 'Thống kê', href: '/accountant/statistics', icon: BarChart3 },
          { name: 'Financial Cases', href: '/accountant/finance', icon: Calculator },
          { 
            name: 'Work Cases', icon: Briefcase,
            children: [
              { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
              { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
              { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
            ]
          },
          { name: 'Thông tin cá nhân', href: '/accountant/profile', icon: User },
        ];
      }
    } else if (userType === 'admin') {
      return [
        { name: 'Dashboard', href: '/admin', icon: Home },
        { name: 'Quản lý đơn hàng', href: '/admin/orders', icon: Package },
        { name: 'Quản lý Design Cases', href: '/admin/design-cases', icon: FileText },
        { name: 'Quản lý Financial Cases', href: '/admin/financial-cases', icon: Calculator },
        { 
          name: 'Work Cases', icon: Briefcase,
          children: [
            { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
            { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
            { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
          ]
        },
        { name: 'Quản lý người dùng', href: '/admin/users', icon: Users },
        { name: 'Quản lý vai trò', href: '/admin/roles', icon: Shield },
        { name: 'Thông tin cá nhân', href: '/admin/profile', icon: User },
      ];
    } else if (userType === 'designer' || isDesigner) {
      return [
        { name: 'Dashboard', href: '/designer', icon: Home },
        { name: 'Design Cases', href: '/designer/cases', icon: FileText },
        { 
          name: 'Work Cases', icon: Briefcase,
          children: [
            { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
            { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
            { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
          ]
        },
        { name: 'Thông tin cá nhân', href: '#', icon: User },
      ];
    } else if (isAdmin) {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Sản phẩm', href: '/dashboard/products', icon: Package },
        { 
          name: 'Work Cases', icon: Briefcase,
          children: [
            { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
            { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
            { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
          ]
        },
        { name: 'Người dùng', href: '/dashboard/users', icon: Users },
        { name: 'Admin', href: '/admin', icon: Shield },
        { name: 'Hồ sơ', href: '/dashboard/profile', icon: User },
      ];
    } else {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Sản phẩm', href: '/dashboard/products', icon: Package },
        { 
          name: 'Work Cases', icon: Briefcase,
          children: [
            { name: 'Thống kê', href: '/work-cases/stats', icon: BarChart3 },
            { name: 'Tôi đã giao', href: '/work-cases/created', icon: Briefcase },
            { name: 'Được giao cho tôi', href: '/work-cases/assigned', icon: Briefcase }
          ]
        },
        { name: 'Hồ sơ', href: '/dashboard/profile', icon: User },
      ];
    }
  };

  const navigation = getNavigation();

  const isActive = (href) => {
    if (!href) return false;
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const isAnyChildActive = (children = []) => children.some((c) => isActive(c.href));
  const toggleSubmenu = (name) => setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${isMobileOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => toggleMobile()} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
                     <div className="flex h-20 items-center justify-between px-4 pt-4">
             <div className="flex flex-col items-center justify-center w-full">
               <img 
                 src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457019/Dvt_Hulo_logo_prc1hv.png" 
                 alt="DVT Hulo Logo" 
                 className="h-12 w-[90%] object-contain"
               />
               <span className="text-xl font-semibold text-gray-700 mt-2">
                 {userType === 'accountant' ? 'Accountant' :
                  userType === 'designer' || isDesigner ? 'Designer' : 
                  isAdmin ? 'Admin' : 'Seller'}
               </span>
             </div>
            <button
              onClick={() => toggleMobile()}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {user && !user?.id_telegram && telegramDeepLink && (
              <div className="mb-2 px-2 py-2 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
                ⚠️ Chưa liên kết Telegram. <a href={telegramDeepLink} target="_blank" rel="noreferrer" className="underline text-blue-600">Liên kết ngay</a>
              </div>
            )}
            {navigation.map((item) => {
              const Icon = item.icon;
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              if (hasChildren) {
                const parentActive = isAnyChildActive(item.children);
                const isOpen = openMenus[item.name] || parentActive;
                return (
                  <div key={item.name} className="">
                    <button
                      type="button"
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                        parentActive ? 'bg-blue-100 text-blue-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="flex items-center">
                        <Icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </span>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    {isOpen && (
                      <div className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = isActive(child.href);
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => toggleMobile()}
                              className={`flex items-center px-2 py-2 text-sm rounded-md ${
                                childActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <ChildIcon className="mr-3 h-4 w-4" />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => toggleMobile()}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Đăng xuất
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-20 items-center px-4 pt-4">
            <div className="flex flex-col items-center justify-center w-full">
              <img 
                src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457019/Dvt_Hulo_logo_prc1hv.png" 
                alt="DVT Hulo Logo" 
                className="h-12 w-[90%] object-contain"
              />
              <span className="text-xl font-semibold text-gray-700 mt-2">
                {userType === 'accountant' ? 'Accountant' :
                 userType === 'designer' || isDesigner ? 'Designer' : 
                 isAdmin ? 'Admin' : 'Seller'}
              </span>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              if (hasChildren) {
                const parentActive = isAnyChildActive(item.children);
                const isOpen = openMenus[item.name] || parentActive;
                return (
                  <div key={item.name} className="">
                    <button
                      type="button"
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
                        parentActive ? 'bg-blue-100 text-blue-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="flex items-center">
                        <Icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </span>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    {isOpen && (
                      <div className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = isActive(child.href);
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`flex items-center px-2 py-2 text-sm rounded-md ${
                                childActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <ChildIcon className="mr-3 h-4 w-4" />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Đăng xuất
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="lg:hidden">
        <button
          onClick={() => toggleMobile()}
          className="text-gray-700 hover:text-gray-900 p-2"
        >
          <Menu size={24} />
        </button>
      </div>
    </>
  );
};

export default Sidebar; 
